import React, { useEffect, useRef, useState } from "react";
import { BadgePercent, Plus, Trash2, Download, X, ImagePlus, Pencil, Share2, Crosshair, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { ccCard, ccInput, ccLabel, ccBtnPink, ccBtnGhost } from "./care/ui";
import ImageCropModal from "./lib/ImageCrop";
import FocalPointModal from "./lib/FocalPoint";

const API = process.env.REACT_APP_BACKEND_URL;
const mediaSrc = (path) => (path && !path.startsWith("http") ? `${API}/api/store/media/${path}` : path);

const PLACEMENTS = [
  { key: "landing", label: "Landing page" },
  { key: "store", label: "Store" },
  { key: "care", label: "Care Connections" },
  { key: "dashboard", label: "Dashboards" },
];

const EMPTY = { title: "", description: "", link_url: "", media_path: "", media_kind: "image", placements: [], focal_x: 50, focal_y: 50, priority: 1, start_date: "", end_date: "", rotate_secs: 3 };

// Schedule status chip: upcoming window, live window, or expired
const scheduleChip = (a) => {
  const today = new Date().toISOString().slice(0, 10);
  if (a.start_date && a.start_date > today) return { text: `Starts ${a.start_date}`, cls: "bg-[#E6F2FA] border-[#B3E0F2] text-[#3D7E9E]" };
  if (a.end_date && a.end_date < today) return { text: "Expired", cls: "bg-gray-100 border-gray-200 text-gray-400" };
  if (a.start_date || a.end_date) return { text: a.end_date ? `Ends ${a.end_date}` : "In window", cls: "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]" };
  return null;
};

// Live per-ad analytics: 30-day views/clicks bar chart + per-placement breakdown
const AdStatsModal = ({ ad, onClose }) => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/master/ads/${ad.id}/stats`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setStats)
      .catch(() => setStats(null));
  }, [ad.id]);
  const maxV = stats ? Math.max(1, ...stats.daily.map((d) => Math.max(d.impressions, d.clicks))) : 1;
  return (
    <div className="fixed inset-0 z-[1200] bg-[#374151]/50 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="ad-stats-modal" onClick={onClose}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-lg w-full p-6 shadow-xl animate-modal-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-1 border-b border-[#FCE6E9] pb-3">
          <h3 className="text-base font-extrabold font-heading text-[#374151]">{ad.title} — Analytics</h3>
          <button onClick={onClose} data-testid="ad-stats-close" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        {!stats ? (
          <p className="text-xs text-[#9CA3AF] font-semibold py-8 text-center">Loading stats…</p>
        ) : (
          <>
            <p className="text-[11px] font-bold text-[#9CA3AF] my-3" data-testid="ad-stats-totals">
              Last 30 days — <span className="text-[#3D7E9E]">■</span> views · <span className="text-[#E5A0BC]">■</span> clicks · All-time: {stats.totals.impressions} views / {stats.totals.clicks} clicks
            </p>
            <div className="flex items-end gap-[3px] h-32 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-3" data-testid="ad-stats-chart">
              {stats.daily.map((d) => (
                <div key={d.date} className="flex-1 flex items-end justify-center gap-[1px] h-full" title={`${d.date}: ${d.impressions} views, ${d.clicks} clicks`} data-testid={`ad-stats-day-${d.date}`}>
                  <div className="w-1.5 bg-[#B3E0F2] rounded-t-sm" style={{ height: `${Math.max(2, (d.impressions / maxV) * 100)}%` }} />
                  <div className="w-1.5 bg-[#F8C8DC] rounded-t-sm" style={{ height: `${Math.max(2, (d.clicks / maxV) * 100)}%` }} />
                </div>
              ))}
            </div>
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mt-4 mb-2">Which pages perform best</p>
            <div className="space-y-1.5" data-testid="ad-stats-placements">
              {stats.placements.length === 0 && <p className="text-[11px] text-[#9CA3AF] font-medium">No traffic yet — stats appear as the ad gets views.</p>}
              {stats.placements.map((p) => (
                <div key={p.placement} className="flex items-center justify-between p-2.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl text-[11px]" data-testid={`ad-stats-placement-${p.placement}`}>
                  <span className="font-bold text-[#374151] capitalize">{p.placement}</span>
                  <span className="font-extrabold text-[#3D7E9E]">
                    {p.impressions} views · <span className="text-[#E5A0BC]">{p.clicks} clicks</span> · {p.impressions ? Math.round((p.clicks / p.impressions) * 1000) / 10 : 0}% CTR
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default function MasterAds() {
  const [ads, setAds] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [focalOpen, setFocalOpen] = useState(false);
  const [statsAd, setStatsAd] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const fileRef = useRef(null);

  const load = () =>
    fetch(`${API}/api/master/ads`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setAds)
      .catch((err) => console.debug("ads fetch failed", err));

  useEffect(() => {
    load();
  }, []);

  const togglePlacement = (p) =>
    setForm((f) => ({ ...f, placements: f.placements.includes(p) ? f.placements.filter((x) => x !== p) : [...f.placements, p] }));

  const uploadCreative = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    // Images go through the cropper first so every creative is display-perfect
    if (file.type.startsWith("image/")) {
      setCropFile(file);
      return;
    }
    await doUpload(file);
  };

  const doUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/store/admin/media`, { method: "POST", credentials: "include", body: fd });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Upload failed");
      setForm((f) => ({ ...f, media_path: body.path, media_kind: body.content_type.startsWith("video") ? "video" : "image" }));
      toast.success("Creative uploaded.");
      if (!body.content_type.startsWith("video")) setFocalOpen(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.placements.length) return toast.error("Pick at least one placement — or several, the same ad can run in multiple locations.");
    try {
      const res = await fetch(editingId ? `${API}/api/master/ads/${editingId}` : `${API}/api/master/ads`, {
        method: editingId ? "PATCH" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not save ad");
      toast.success(editingId ? "Ad updated — changes are live." : "Ad is live in the selected placements.");
      setForm(EMPTY);
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({
      title: a.title || "",
      description: a.description || "",
      link_url: a.link_url || "",
      media_path: a.media_path || "",
      media_kind: a.media_kind || "image",
      placements: a.placements || [],
      rate_monthly: a.rate_monthly ?? 0,
      rate_cpm: a.rate_cpm ?? 0,
      rate_cpc: a.rate_cpc ?? 0,
      focal_x: a.focal_x ?? 50,
      focal_y: a.focal_y ?? 50,
      priority: a.priority ?? 1,
      start_date: a.start_date || "",
      end_date: a.end_date || "",
      rotate_secs: a.rotate_secs ?? 3,
    });
    setShowForm(true);
  };

  const shareAd = async (a) => {
    const url = `${API}/api/share/ad/${a.id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: a.title, text: a.description || a.title, url });
        return;
      } catch {
        /* user dismissed the share sheet */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied — rich preview included for socials.");
    } catch {
      window.prompt("Copy the share link:", url);
    }
  };

  const toggle = async (id) => {
    await fetch(`${API}/api/master/ads/${id}/toggle`, { method: "POST", credentials: "include" });
    load();
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete ad "${title}" and its tracking data?`)) return;
    try {
      const res = await fetch(`${API}/api/master/ads/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error();
      toast.success("Ad deleted.");
      load();
    } catch {
      toast.error("Could not delete ad.");
    }
  };

  const downloadReport = async (ad) => {
    try {
      const res = await fetch(`${API}/api/master/ads/${ad.id}/events`, { credentials: "include" });
      const body = await res.json();
      if (!res.ok) throw new Error();
      const clicks = body.events.filter((e) => e.type === "click");
      const pdf = new jsPDF();
      pdf.setFontSize(18);
      pdf.text("NestTrack Sponsor Ad Report", 14, 18);
      pdf.setFontSize(12);
      pdf.text(ad.title, 14, 30);
      pdf.setFontSize(10);
      pdf.text(`Placements: ${(ad.placements || []).join(", ") || "none"}`, 14, 40);
      pdf.text(`Impressions: ${ad.impressions}   Clicks: ${ad.clicks}   CTR: ${ad.ctr}%`, 14, 48);
      pdf.text(`Created: ${new Date(ad.createdAt).toLocaleString()}   Status: ${ad.active ? "Active" : "Paused"}`, 14, 56);
      pdf.setFontSize(12);
      pdf.text(`Recent clicks (${clicks.length}):`, 14, 68);
      pdf.setFontSize(9);
      clicks.slice(0, 25).forEach((c, i) => {
        pdf.text(`${new Date(c.ts).toLocaleString()}  —  placement: ${c.placement || "unknown"}`, 14, 76 + i * 6);
      });
      pdf.save(`nesttrack-ad-${ad.id}.pdf`);
      toast.success("PDF report downloaded.");
    } catch {
      toast.error("Could not build the report.");
    }
  };

  return (
    <div className={`${ccCard} p-6`} data-testid="master-ads">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h3 className="text-base font-extrabold font-heading flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-[#FFF2D1] flex items-center justify-center">
            <BadgePercent className="w-4 h-4 text-[#D9A93E]" />
          </span>
          Sponsor Ads
          <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{(ads || []).filter((a) => a.active).length} live</span>
          <span className="text-[10px] font-bold text-[#2F6031] uppercase tracking-wider" data-testid="ads-total-revenue">
            est. revenue ${(ads || []).reduce((n, a) => n + (a.revenue || 0), 0).toFixed(2)}
          </span>
        </h3>
        <button onClick={() => { setEditingId(null); setForm(EMPTY); setShowForm(!showForm); }} className={ccBtnPink} data-testid="ads-create-btn">
          <Plus className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> New Ad
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-5 space-y-3 mb-5 dark:bg-[#232D3D] dark:border-[#374457]" data-testid="ads-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={ccLabel}>Ad Title</label>
              <input required type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={ccInput} data-testid="ad-title-input" placeholder="Sunny Snacks Co." />
            </div>
            <div>
              <label className={ccLabel}>Link URL (optional)</label>
              <input type="url" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} className={ccInput} data-testid="ad-link-input" placeholder="https://…" />
            </div>
          </div>
          <div>
            <label className={ccLabel}>Brief Description</label>
            <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={ccInput} data-testid="ad-desc-input" placeholder="Healthy snacks for little ones — 20% off for NestTrack families" />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" data-testid="ad-media-input" onChange={uploadCreative} />
            {cropFile && (
              <ImageCropModal
                file={cropFile}
                defaultAspect={16 / 9}
                onCancel={() => setCropFile(null)}
                onDone={async (cropped) => {
                  setCropFile(null);
                  await doUpload(cropped);
                }}
              />
            )}
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className={ccBtnGhost} data-testid="ad-upload-btn">
              <ImagePlus className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> {uploading ? "Uploading…" : form.media_path ? "Replace Creative" : "Upload Photo / Video"}
            </button>
            {form.media_path && (
              <span className="flex items-center gap-2 text-[11px] font-bold text-[#2F6031]">
                {form.media_kind === "video" ? (
                  <video src={mediaSrc(form.media_path)} className="w-16 h-10 rounded-lg object-cover" muted />
                ) : (
                  <img src={mediaSrc(form.media_path)} alt="" className="w-16 h-10 rounded-lg object-cover" style={{ objectPosition: `${form.focal_x}% ${form.focal_y}%` }} />
                )}
                attached
                {form.media_kind === "image" && (
                  <button type="button" onClick={() => setFocalOpen(true)} data-testid="ad-focal-btn" className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#B3E0F2]/40 border border-[#B3E0F2] text-[#3D7E9E] hover:bg-[#B3E0F2]/70 transition-colors">
                    <Crosshair className="w-3 h-3" /> Adjust focus
                  </button>
                )}
                <button type="button" onClick={() => setForm({ ...form, media_path: "" })} data-testid="ad-media-remove" className="text-[#B04A6B]">
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            )}
            {focalOpen && form.media_path && form.media_kind === "image" && (
              <FocalPointModal
                src={mediaSrc(form.media_path)}
                initial={{ x: form.focal_x ?? 50, y: form.focal_y ?? 50 }}
                onCancel={() => setFocalOpen(false)}
                onDone={(p) => {
                  setForm((f) => ({ ...f, focal_x: p.x, focal_y: p.y }));
                  setFocalOpen(false);
                  toast.success("Focus point saved — the ad will always center on it.");
                }}
              />
            )}
          </div>
          <div>
            <label className={ccLabel}>Rotation Priority (1 shows first — ads change over every 3 seconds)</label>
            <input type="number" min="1" step="1" value={form.priority ?? 1} onChange={(e) => setForm({ ...form, priority: Math.max(1, parseInt(e.target.value, 10) || 1) })} className={ccInput} data-testid="ad-priority-input" placeholder="1" />
          </div>
          <div>
            <label className={ccLabel}>Schedule (optional — promo auto-starts and auto-expires)</label>
            <div className="grid grid-cols-3 gap-2.5">
              <input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={ccInput} data-testid="ad-start-date" title="Start date" />
              <input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={ccInput} data-testid="ad-end-date" title="End date" />
              <select value={form.rotate_secs ?? 3} onChange={(e) => setForm({ ...form, rotate_secs: Number(e.target.value) })} className={ccInput} data-testid="ad-rotate-secs" title="Seconds this ad shows per changeover">
                <option value={3}>Rotate 3s</option>
                <option value={5}>Rotate 5s</option>
                <option value={10}>Rotate 10s</option>
              </select>
            </div>
          </div>
          <div>
            <label className={ccLabel}>Rate Card (optional — powers revenue tracking)</label>
            <div className="grid grid-cols-3 gap-2.5">
              <input type="number" min="0" step="0.01" value={form.rate_monthly ?? ""} onChange={(e) => setForm({ ...form, rate_monthly: e.target.value === "" ? 0 : Number(e.target.value) })} className={ccInput} data-testid="ad-rate-monthly" placeholder="$/mo flat" />
              <input type="number" min="0" step="0.01" value={form.rate_cpm ?? ""} onChange={(e) => setForm({ ...form, rate_cpm: e.target.value === "" ? 0 : Number(e.target.value) })} className={ccInput} data-testid="ad-rate-cpm" placeholder="CPM $/1k" />
              <input type="number" min="0" step="0.01" value={form.rate_cpc ?? ""} onChange={(e) => setForm({ ...form, rate_cpc: e.target.value === "" ? 0 : Number(e.target.value) })} className={ccInput} data-testid="ad-rate-cpc" placeholder="CPC $/click" />
            </div>
          </div>
          <div>
            <label className={ccLabel}>Placements (pick one or more — same ad, multiple locations)</label>
            <div className="flex gap-2 flex-wrap" data-testid="ad-placements">
              {PLACEMENTS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => togglePlacement(p.key)}
                  data-testid={`ad-placement-${p.key}`}
                  className={`px-4 py-2 rounded-full text-xs font-bold border transition-all ${form.placements.includes(p.key) ? "bg-[#B3E0F2] border-[#9ACCE0] text-[#374151]" : "bg-white border-[#FCE6E9] text-[#6B7280] hover:border-[#B3E0F2] dark:bg-[#273244] dark:border-[#374457]"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY); }} className={ccBtnGhost}>Cancel</button>
            <button type="submit" className={ccBtnPink} data-testid="ad-submit-btn">{editingId ? "Save Changes" : "Publish Ad"}</button>
          </div>
        </form>
      )}

      {ads === null ? (
        <p className="text-xs text-[#9CA3AF] font-semibold">Loading ads…</p>
      ) : ads.length === 0 ? (
        <p className="text-xs text-[#9CA3AF] font-semibold" data-testid="ads-empty">No ads yet — create your first sponsor placement.</p>
      ) : (
        <div className="space-y-2" data-testid="ads-list">
          {ads.map((a) => (
            <div key={a.id} className={`flex items-center justify-between gap-3 p-3.5 rounded-2xl border ${a.active ? "bg-[#FFF9F2] border-[#FCE6E9]" : "bg-gray-50 border-gray-200 opacity-60"} dark:bg-[#273244] dark:border-[#374457]`} data-testid={`ads-row-${a.id}`}>
              <div className="min-w-0">
                <p className="text-xs font-black text-[#374151] dark:text-slate-100 truncate">
                  {a.title}
                  <span className={`ml-2 px-2 py-0.5 border text-[9px] font-extrabold uppercase rounded-full ${a.active ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]" : "bg-gray-100 border-gray-200 text-gray-400"}`}>
                    {a.active ? "Live" : "Paused"}
                  </span>
                </p>
                <p className="text-[10px] text-[#9CA3AF] font-medium">
                  <span className="px-1.5 py-0.5 bg-[#B3E0F2]/40 border border-[#B3E0F2] rounded-full text-[#3D7E9E] font-extrabold mr-1" data-testid={`ads-priority-${a.id}`}>P{a.priority ?? 1}</span>
                  {scheduleChip(a) && (
                    <span className={`px-1.5 py-0.5 border rounded-full font-extrabold mr-1 ${scheduleChip(a).cls}`} data-testid={`ads-schedule-${a.id}`}>{scheduleChip(a).text}</span>
                  )}
                  → {(a.placements || []).join(", ") || "no placements"} · {a.impressions} views · {a.clicks} clicks · CTR {a.ctr}%
                  {(a.rate_monthly || a.rate_cpm || a.rate_cpc) ? (
                    <span className="text-[#2F6031] font-extrabold" data-testid={`ads-revenue-${a.id}`}> · est. revenue ${Number(a.revenue || 0).toFixed(2)} ({a.days_live}d live)</span>
                  ) : null}
                </p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => setStatsAd(a)} title="Live analytics" className="w-8 h-8 rounded-full bg-white border border-[#B3E0F2] flex items-center justify-center text-[#3D7E9E] hover:bg-[#E6F2FA]" data-testid={`ads-stats-${a.id}`}>
                  <BarChart3 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => downloadReport(a)} title="Download PDF report" className="w-8 h-8 rounded-full bg-white border border-[#B3E0F2] flex items-center justify-center text-[#3D7E9E] hover:bg-[#E6F2FA]" data-testid={`ads-report-${a.id}`}>
                  <Download className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => shareAd(a)} title="Share ad (rich link preview)" className="w-8 h-8 rounded-full bg-white border border-[#C4E5C2] flex items-center justify-center text-[#2F6031] hover:bg-[#E2F4E1]" data-testid={`ads-share-${a.id}`}>
                  <Share2 className="w-3 h-3" />
                </button>
                <button onClick={() => startEdit(a)} title="Edit ad" className="px-3 py-1.5 bg-white border border-[#F5E3B3] rounded-full text-[10px] font-bold text-[#374151] hover:bg-[#FFF2D1]" data-testid={`ads-edit-${a.id}`}>
                  <Pencil className="w-3 h-3 inline -mt-0.5" /> Edit
                </button>
                <button onClick={() => toggle(a.id)} className="px-3 py-1.5 bg-white border border-[#F5E3B3] rounded-full text-[10px] font-bold text-[#A08244] hover:bg-[#FFF2D1]" data-testid={`ads-toggle-${a.id}`}>
                  {a.active ? "Pause" : "Resume"}
                </button>
                <button onClick={() => remove(a.id, a.title)} className="w-8 h-8 rounded-full bg-white border border-[#F8C8DC] flex items-center justify-center text-[#B04A6B] hover:bg-[#FCE6E9]" data-testid={`ads-delete-${a.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {statsAd && <AdStatsModal ad={statsAd} onClose={() => setStatsAd(null)} />}
    </div>
  );
}
